import { type IProperty, Property } from '@app/land-hold/property/models/Property';
import { useForm } from '@shared/composables/useForm';
import { useRoute } from '@shared/composables/useRoute';
import { usePayload } from '@shared/composables/usePayload';
import { useInjectErrorState } from '@shared/composables/useProvideErrorState';
import { useWithValidationConfig } from '@shared/composables/useWithValidationConfig';
import type { IPropertyVersion } from '@app/land-hold/property/models/PropertyVersion';
import type { FormOptions } from '@shared/types/Form';
import { useFetchListData } from '@shared/composables/useFetchListData.ts';
import { route } from 'ziggy-js';
import type { ListResponse } from '@shared/types/ApiResponse';

export function usePropertyRepository<T extends IProperty = IProperty>() {
    const command = () => {
        const store = (data: T, options?: FormOptions) => {
            const hasError = useInjectErrorState();
            const form = useForm<T>(data).precognition(
                Property.routes().store,
                useWithValidationConfig(hasError, () => form, options),
            );

            function execute(options?: FormOptions) {
                form.transform((data) =>
                    usePayload<T>(data, {
                        exclude: Property.create(data).hidden(),
                        nullable: ['start_date', 'end_date', 'buffer', 'carbon_loss', 'additional_info'],
                    }),
                ).submit(options);
            }

            return { form, execute, hasError };
        };

        const update = (id: string, data: T, options?: FormOptions) => {
            const hasError = useInjectErrorState();
            const form = useForm<T>(data).precognition(
                {
                    url: useRoute().build(Property.routes().update, {
                        id,
                    }),
                    method: Property.routes().update.method,
                },
                useWithValidationConfig(hasError, () => form, options),
            );

            function execute(options?: FormOptions) {
                form.transform((data) =>
                    usePayload<T>(data, {
                        exclude: Property.create(data).hidden(),
                        nullable: ['start_date', 'end_date', 'buffer', 'carbon_loss', 'additional_info'],
                    }),
                ).submit(options);
            }

            return { form, execute };
        };

        const updateInventoryGenerationPause = (id: string, paused: boolean, options?: FormOptions) => {
            const hasError = useInjectErrorState();
            const form = useForm<{ paused: boolean }>({ paused }).precognition(
                {
                    url: useRoute().build(Property.routes().updateInventoryGenerationPause, {
                        id,
                    }),
                    method: Property.routes().updateInventoryGenerationPause.method,
                },
                useWithValidationConfig(hasError, () => form, options),
            );

            function execute(options?: FormOptions) {
                form.submit(options);
            }

            return { form, execute };
        };

        const destroy = <U extends { id: string }>(id: string, options?: FormOptions) => {
            const hasError = useInjectErrorState();
            const form = useForm<U>({ id } as U).precognition(
                {
                    url: useRoute().build(Property.routes().destroy, {
                        id,
                    }),
                    method: Property.routes().destroy.method,
                },
                useWithValidationConfig(hasError, () => form, options),
            );

            function execute(options?: FormOptions) {
                form.submit(options);
            }

            return { form, execute };
        };

        const storeVersion = <T extends IPropertyVersion>(propertyId: string, data: T, options?: FormOptions) => {
            const hasError = useInjectErrorState();
            const form = useForm<T>(data).precognition(
                {
                    url: useRoute().build(Property.routes().versions.store, { id: propertyId }),
                    method: Property.routes().versions.store.method,
                },
                useWithValidationConfig(hasError, () => form, options),
            );

            function execute(options?: FormOptions) {
                form.transform((data) => usePayload<T>(data)).submit(options);
            }

            return { form, execute, hasError };
        };

        const updateVersion = <T extends IPropertyVersion>(propertyId: string, versionId: string, data: T, options?: FormOptions) => {
            const hasError = useInjectErrorState();
            const form = useForm<T>(data).precognition(
                {
                    url: useRoute().build(Property.routes().versions.update, { id: propertyId, version_id: versionId }),
                    method: Property.routes().versions.update.method,
                },
                useWithValidationConfig(hasError, () => form, options),
            );

            function execute(options?: FormOptions) {
                form.transform((data) => usePayload<T>(data)).submit(options);
            }

            return { form, execute, hasError };
        };

        const updateVersionReason = <T extends Pick<IPropertyVersion, 'reason_for_change'>>(propertyId: string, versionId: string, data: T, options?: FormOptions) => {
            const hasError = useInjectErrorState();
            const form = useForm<T>(data).precognition(
                {
                    url: useRoute().build(Property.routes().versions.updateReason, { id: propertyId, version_id: versionId }),
                    method: Property.routes().versions.updateReason.method,
                },
                useWithValidationConfig(hasError, () => form, options),
            );

            function execute(options?: FormOptions) {
                form.transform((data) => usePayload<T>(data)).submit(options);
            }

            return { form, execute, hasError };
        };

        return {
            store,
            update,
            updateInventoryGenerationPause,
            destroy,
            storeVersion,
            updateVersion,
            updateVersionReason,
        };
    };

    const query = () => {
        const show = (id: string) => {
            return { endpoint: useRoute().build(Property.routes().show, { id }) };
        };

        const availableVersionYears = (id: string) => {
            return { endpoint: useRoute().build(Property.routes().availableVersionYears.index, { id }) };
        };

        const list = () => {
            const { data: properties, processing: fetchingProperties } = useFetchListData<Property>(useRoute().build(Property.routes().index), 'appSection@property::IndexPropertiesPage', (payload) => Property.from(payload));
            return { properties, fetchingProperties };
        };

        const listAllowedFor = (stewardId: string) => {
            const data = ref<IProperty[]>([]);
            const processing = ref(true);

            window.axios
                .get<ListResponse<IProperty>>(route('stewards.eligible-properties', { id: stewardId }))
                .then((response) => {
                    data.value = response.data.data;
                })
                .catch((error) => {
                    console.error('Failed to load eligible properties for steward', error);
                })
                .finally(() => {
                    processing.value = false;
                });

            return { data, processing };
        };

        return {
            show,
            availableVersionYears,
            list,
            listAllowedFor,
        };
    };

    return {
        command: command(),
        query: query(),
    };
}
